import { ARCHIVE_SEARCH_MODES, ARCHIVE_SEARCH_SORTS } from "./archiveSearchProtocol.js";
import { normalizeArchiveSearchIndex } from "./archiveSearchIndex.js";
import { sortSearchResults } from "./lexicalSearch.js";
import { clampSearchQuery, normalizeSearchText } from "./searchText.js";

const viteEnv = import.meta.env || {};

export const ARCHIVE_SEMANTIC_SEARCH_PATH = viteEnv.VITE_ARCHIVE_SEMANTIC_SEARCH_PATH || "/api/v1/archive/semantic-search";
export const ARCHIVE_SIMILAR_SHOWS_PATH = "/api/v1/archive/similar";

export function getShowsLikeAnchorTitle(query = "") {
  const normalizedQuery = normalizeSearchText(query);
  const match = normalizedQuery.match(/^(?:shows?\s+like|something\s+like|more\s+like|like)\s+(.+?)(?:\s+but\b.*)?$/);
  return match?.[1]?.trim() || "";
}

export function getShowsLikeAnchorShow(index = {}, query = "") {
  const anchorTitle = getShowsLikeAnchorTitle(query);
  if (!anchorTitle) {
    return null;
  }
  const normalizedIndex = index?.showsBySlug ? index : normalizeArchiveSearchIndex(index);
  return (normalizedIndex.shows || []).find((show) => normalizeSearchText(show.title) === anchorTitle) || null;
}

export async function runSemanticSearch({
  query,
  corpusVersion,
  limit = 24,
  fetchImpl = globalThis.fetch?.bind(globalThis),
  signal,
} = {}) {
  const rawQuery = clampSearchQuery(query).trim();
  if (!rawQuery) {
    throw new Error("query is required");
  }
  if (!fetchImpl) {
    throw new Error("fetch is not available");
  }

  const response = await fetchImpl(ARCHIVE_SEMANTIC_SEARCH_PATH, {
    method: "POST",
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    signal,
    body: JSON.stringify({
      query: rawQuery,
      corpusVersion: corpusVersion || null,
      limit,
    }),
  });

  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message = payload?.error || payload?.reason || `Semantic search failed: ${response.status}`;
    throw new Error(message);
  }
  return payload;
}

export async function runSimilarShowsSearch({
  slug,
  fetchImpl = globalThis.fetch?.bind(globalThis),
  signal,
} = {}) {
  if (!slug) {
    throw new Error("slug is required");
  }
  if (!fetchImpl) {
    throw new Error("fetch is not available");
  }

  const response = await fetchImpl(`${ARCHIVE_SIMILAR_SHOWS_PATH}/${encodeURIComponent(slug)}`, {
    method: "GET",
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
    },
    signal,
  });

  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message = payload?.error || `Similar shows search failed: ${response.status}`;
    throw new Error(message);
  }
  return payload;
}

// matchTags are the show's tags that share a word with the search.
function semanticReason(matchTags = []) {
  const tags = matchTags.filter(Boolean).slice(0, 2).join(", ");
  return tags ? `Matches ${tags}` : "Matches your search";
}

export function hydrateSemanticResults(index, semanticPayload = {}, { sort = ARCHIVE_SEARCH_SORTS.RELEVANCE, query = "" } = {}) {
  const normalizedIndex = index?.showsBySlug ? index : normalizeArchiveSearchIndex(index);
  const normalizedQuery = normalizeSearchText(query);
  const unknownSlugs = [];
  const results = [];

  for (const item of semanticPayload?.results || []) {
    const show = normalizedIndex.showsBySlug.get(item.slug);
    if (!show) {
      unknownSlugs.push(item.slug);
      continue;
    }
    const score = Number(item.score || 0);
    const titleMatch = normalizedQuery && normalizeSearchText(show.title) === normalizedQuery;
    results.push({
      ...show,
      score: titleMatch ? 1.5 : score,
      semanticScore: titleMatch ? 1.5 : score,
      matchMode: semanticPayload.mode || ARCHIVE_SEARCH_MODES.SEMANTIC,
      matchTags: Array.isArray(item.matchTags) ? item.matchTags : [],
      matchReason: titleMatch ? "Exact title match" : semanticReason(Array.isArray(item.matchTags) ? item.matchTags : []),
    });
  }

  return {
    mode: semanticPayload.mode || ARCHIVE_SEARCH_MODES.SEMANTIC,
    corpusVersion: semanticPayload.corpusVersion || null,
    threshold: semanticPayload.threshold ?? null,
    topScore: semanticPayload.topScore || results[0]?.score || 0,
    unknownSlugs,
    results: sortSearchResults(results, sort),
  };
}

export function hydrateSimilarShowResults(index, similarPayload = {}, { anchorTitle = "", sort = ARCHIVE_SEARCH_SORTS.RELEVANCE } = {}) {
  const normalizedIndex = index?.showsBySlug ? index : normalizeArchiveSearchIndex(index);
  const unknownSlugs = [];
  const results = [];

  for (const item of similarPayload?.results || []) {
    const show = normalizedIndex.showsBySlug.get(item.slug);
    if (!show) {
      unknownSlugs.push(item.slug);
      continue;
    }
    const score = Number(item.score || 0);
    results.push({
      ...show,
      score,
      semanticScore: score,
      matchMode: ARCHIVE_SEARCH_MODES.SEMANTIC,
      matchTags: [],
      matchReason: anchorTitle ? `Similar to ${anchorTitle}` : "Similar show",
    });
  }

  return {
    mode: ARCHIVE_SEARCH_MODES.SEMANTIC,
    corpusVersion: similarPayload.corpusVersion || null,
    threshold: null,
    topScore: results[0]?.score || 0,
    unknownSlugs,
    results: sortSearchResults(results, sort),
  };
}
